import { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { DEFAULT_PRESET_NAME } from './presets/defaultPreset';

const MAX_NAME_LEN = 40;

function validateName(name, existingNames, editingName) {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required.';
  if (trimmed.length > MAX_NAME_LEN) return `Name must be ${MAX_NAME_LEN} characters or fewer.`;
  if (trimmed.toLowerCase() === DEFAULT_PRESET_NAME.toLowerCase()) {
    return `"${DEFAULT_PRESET_NAME}" is a reserved name.`;
  }
  const collision = existingNames.some(
    (n) => n !== editingName && n.toLowerCase() === trimmed.toLowerCase()
  );
  if (collision) return 'A preset with that name already exists.';
  return null;
}

export function SavePresetModal({
  show,
  onHide,
  activePreset,
  activeIsDefault,
  canSaveMore,
  existingNames,
  onSubmit,
}) {
  const canUpdate = !activeIsDefault && activePreset && !activePreset.isBuiltIn;
  const initialMode = canUpdate ? 'update' : 'create';

  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (show) {
      setMode(canUpdate ? 'update' : 'create');
      setName('');
      setLocalError(null);
      setSubmitting(false);
    }
  }, [show, canUpdate]);

  const nameError = mode === 'create' ? validateName(name, existingNames, null) : null;
  const canSubmit =
    !submitting &&
    (mode === 'update' || (mode === 'create' && !nameError && canSaveMore));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setLocalError(null);
    try {
      await onSubmit({ mode, name });
    } catch (err) {
      setLocalError(err?.message || 'Failed to save preset.');
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Save Preset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canUpdate && (
            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                id="save-mode-update"
                name="save-mode"
                label={`Update "${activePreset.name}"`}
                checked={mode === 'update'}
                onChange={() => setMode('update')}
              />
              <Form.Check
                type="radio"
                id="save-mode-create"
                name="save-mode"
                label={
                  canSaveMore
                    ? 'Save as new preset'
                    : 'Save as new preset (limit reached)'
                }
                checked={mode === 'create'}
                onChange={() => setMode('create')}
                disabled={!canSaveMore}
              />
            </Form.Group>
          )}

          {!canUpdate && !canSaveMore && (
            <Alert variant="warning" className="mb-3">
              Preset limit reached. Delete an existing preset before saving a new
              one.
            </Alert>
          )}

          {mode === 'create' && (
            <Form.Group className="mb-3">
              <Form.Label>Preset name</Form.Label>
              <Form.Control
                type="text"
                autoFocus
                maxLength={MAX_NAME_LEN}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Exploration West"
                isInvalid={!!nameError && name.length > 0}
                disabled={!canSaveMore}
              />
              {nameError && name.length > 0 && (
                <Form.Control.Feedback type="invalid">
                  {nameError}
                </Form.Control.Feedback>
              )}
              <Form.Text className="text-muted">
                {name.length}/{MAX_NAME_LEN}
              </Form.Text>
            </Form.Group>
          )}

          {mode === 'update' && canUpdate && (
            <p className="text-muted mb-0">
              The current map configuration will overwrite "
              <strong>{activePreset.name}</strong>".
            </p>
          )}

          {localError && <Alert variant="danger" className="mt-3 mb-0">{localError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
